/* eslint-disable react/prop-types */
import { useMemo } from 'react';
import { Box, DataTable, Typography } from '@barracuda-internal/bds-core-v2';
import { type MRT_ColumnDef } from 'material-react-table';
import { Policy } from 'src/types/policyTypes';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from 'src/redux/store';
import { reorderPolicyFeeds } from 'src/features/policyengine/policyEngineSlice';
import { PolicyTagRendererCell, DirectoryOrIdentityRenderCell, ConvertDateAndTimeCell } from './Renderer';
import PolicyRowActions from './PolicyRowActions';

interface GridPanelProps {
	policyFeeds: Policy[];
	isLoading: boolean;
}

export default function GridPanel({ policyFeeds, isLoading }: GridPanelProps) {
	const dispatch = useDispatch<AppDispatch>();
	const { policyChange } = useSelector((state: RootState) => state.policyEngine);

	const columns = useMemo<MRT_ColumnDef<Policy>[]>(
		() => [
			{
				accessorKey: 'id',
				header: 'Priority',
				size: 60,
				enableSorting: false,
				Cell: ({ row }) => (
					<Typography component="span">{row.index + 1}</Typography>
				),
			},
			{
				accessorKey: 'name',
				header: 'Policy name',
				size: 140,
				enableSorting: false,
				Cell: ({ row }) => row.original?.name,
			},
			{
				accessorKey: 'conditions.findingTags',
				header: 'Classifier',
				size: 180,
				enableSorting: false,
				Cell: ({ row }) => PolicyTagRendererCell(row.original),
			},
			{
				accessorKey: 'conditions.identity',
				header: 'Directory/Identity',
				size: 140,
				enableSorting: false,
				Cell: ({ row }) => DirectoryOrIdentityRenderCell(row.original?.conditions),
			},
			{
				accessorKey: 'conditions.identity.authorship',
				header: 'Authorship',
				size: 100,
				enableSorting: false,
				Cell: ({ row }) => row.original?.conditions?.identity?.authorship?.join(', '),
			},
			{
				accessorKey: 'actions',
				header: 'Rule action',
				size: 90,
				enableSorting: false,
				Cell: ({ row }) => `${row.original?.actions}`,
			},
			{
				accessorKey: 'createdBy.name',
				header: 'Created by',
				size: 100,
				enableSorting: false,
				Cell: ({ row }) => row.original?.createdBy?.name,
			},
			{
				accessorKey: 'lastModified',
				header: 'Last modified',
				size: 100,
				enableSorting: false,
				Cell: ({ row }) => ConvertDateAndTimeCell(row.original?.lastModified),
			},
			{
				accessorKey: 'disabled',
				header: 'Status',
				size: 120,
				enableSorting: false,
				Cell: ({ row }) => <PolicyRowActions feed={row.original} />,
			},
		],
		[],
	);

	return (
		<>
			<Box sx={{ marginTop: '30px' }}>
				<DataTable<Policy>
					columns={columns}
					data={policyFeeds}
					getRowId={(row) => row.id}
					showSearchFields={false}
					enableRowOrdering={policyChange}
					enableSorting={false}
					enableColumnActions={false}
					enableColumnFilters={false}
					enableGlobalFilter={false}
					enablePagination={false}
					enableTopToolbar={false}
					enableBottomToolbar={false}
					enableColumnDragging={false}
					layoutMode="semantic"
					muiRowDragHandleProps={({ table }) => ({
						onDragEnd: () => {
							const { draggingRow, hoveredRow } = table.getState();
							if (draggingRow && hoveredRow && draggingRow.index !== hoveredRow.index) {
								dispatch(reorderPolicyFeeds({ dragIndex: draggingRow.index, hoverIndex: hoveredRow.index }));
							}
						},
					})}
					muiTableBodyRowProps={{
						sx: { height: '90px' },
					}}
					muiTableContainerProps={{
						sx: { boxShadow: 'none' },
					}}
					state={{
						isLoading,
					}}
					localization={{
						noRecordsToDisplay: '',
					}}
				/>
			</Box>
			{policyFeeds.length === 0 && !isLoading && (
				<Typography sx={{ textAlign: 'center', marginTop: '20px' }}>
					No results found
				</Typography>
			)}
			<Box sx={{ height: '50px' }} />
		</>
	);
}
